import React, { useState } from 'react';
import { useParams, useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import { useQuery } from 'react-query';
import {
  Container,
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardActionArea,
  CardMedia,
  CardContent,
  Chip,
  CircularProgress,
  Alert,
  Pagination,
  IconButton,
  Tooltip,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { tagManagementAPI, authAPI } from '../services/api';
import { colors } from '../theme';

// Map URL tagType param → { apiSlug, label }
const TAG_TYPE_MAP = {
  service_types: { apiSlug: 'service-types', label: 'Service Type' },
  keywords: { apiSlug: 'keywords', label: 'Keyword' },
  beneficiaries: { apiSlug: 'beneficiaries', label: 'Beneficiary' },
  beneficiary_organizations: { apiSlug: 'beneficiary-organizations', label: 'Beneficiary Organization' },
  co_organizers: { apiSlug: 'co-organizers', label: 'Co-organizer' },
};

const PAGE_SIZE = 24;

function PageCard({ page }) {
  const year = page.issue_year;
  const quarter = page.issue_quarter;
  const label = quarter ? `${year} Q${quarter}` : String(year);

  return (
    <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardActionArea component={RouterLink} to={`/pages/${page.id}`} sx={{ flexGrow: 1 }}>
        {page.thumbnail ? (
          <CardMedia
            component="img"
            image={page.thumbnail}
            alt={`Page ${page.page_number}`}
            sx={{ height: 160, objectFit: 'cover' }}
          />
        ) : (
          <Box
            sx={{
              height: 160,
              bgcolor: 'grey.100',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography variant="caption" color="text.secondary">No image</Typography>
          </Box>
        )}
        <CardContent sx={{ pb: '8px !important' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
            <Chip label={label} size="small" sx={{ fontSize: '0.7rem' }} />
            <Chip label={`P.${page.page_number}`} size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} />
          </Box>
          {page.title && (
            <Typography variant="body2" fontWeight={600} noWrap>{page.title}</Typography>
          )}
          {page.club_name && (
            <Typography variant="caption" color="text.secondary" noWrap>{page.club_name}</Typography>
          )}
        </CardContent>
      </CardActionArea>
    </Card>
  );
}

const AdminTagPagesPage = () => {
  const { tagType, tagId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [currentPage, setCurrentPage] = useState(1);

  React.useEffect(() => {
    if (!authAPI.isAuthenticated() || !authAPI.isAdmin()) {
      navigate('/');
    }
  }, [navigate]);

  const meta = TAG_TYPE_MAP[tagType];
  const apiSlug = meta?.apiSlug ?? tagType;

  // Fetch pages for this tag
  const { data: pagesData, isLoading, error } = useQuery(
    ['tag-pages', apiSlug, tagId, currentPage],
    () => tagManagementAPI.getTagPages(apiSlug, tagId, { page: currentPage, page_size: PAGE_SIZE }),
    { keepPreviousData: true, enabled: !!tagId }
  );

  const pages = pagesData?.data?.results ?? [];
  const totalCount = pagesData?.data?.count ?? 0;
  const totalPages = Math.ceil(totalCount / PAGE_SIZE) || 1;

  // Try to get tag name from router state passed by AdminTagsPage
  const tagName = location.state?.tagName ?? `Tag #${tagId}`;
  const typeLabel = meta?.label ?? tagType;

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'flex-start', gap: 1 }}>
        <Tooltip title="Back to Tag Management">
          <IconButton onClick={() => navigate('/admin/tags')} sx={{ mt: 0.25 }}>
            <ArrowBackIcon />
          </IconButton>
        </Tooltip>
        <Box>
          <Typography variant="h4" fontWeight={700} color={colors.royalBlue}>
            {tagName}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
            {typeLabel} · {totalCount} page{totalCount !== 1 ? 's' : ''} tagged
          </Typography>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>Failed to load pages.</Alert>
      )}

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : pages.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">No pages tagged with this tag.</Typography>
        </Paper>
      ) : (
        <>
          <Grid container spacing={2}>
            {pages.map((page) => (
              <Grid item key={page.id} xs={6} sm={4} md={3} lg={2}>
                <PageCard page={page} />
              </Grid>
            ))}
          </Grid>

          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={(_, v) => setCurrentPage(v)}
                color="primary"
              />
            </Box>
          )}
        </>
      )}
    </Container>
  );
};

export default AdminTagPagesPage;
